import React, { useState, useMemo, useEffect } from "react";
import Table from "../../utils/Table";
import { fetchRegisteredUsers } from "../../services/api";
import { formatDate } from "../../utils/TableUtils";
import { useToast } from "../../contexts/ToastContext";

function RegisteredUsers() {
  const [data, setData] = useState([]);
  const [filterInput, setFilterInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const { showToast } = useToast();
  const pageSize = 10;

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchRegisteredUsers(pageIndex + 1, pageSize, filterInput);
      if (response.success) {
        setData(response.data || []);
        setPageCount(response.pagination?.totalPages || 0);
        setTotalCount(response.pagination?.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching user accounts:", error);
      showToast("Failed to load user accounts.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, filterInput]);

  const handlePageChange = (newPageIndex) => {
    setPageIndex(newPageIndex);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: row => `${row.firstName} ${row.lastName}`,
        Cell: ({ row }) => `${row.original.firstName} ${row.original.lastName}`,
        sortType: (rowA, rowB) => {
          const a = `${rowA.original.firstName} ${rowA.original.lastName}`.toLowerCase();
          const b = `${rowB.original.firstName} ${rowB.original.lastName}`.toLowerCase();
          return a.localeCompare(b);
        }
      },
      {
        Header: "Email Address",
        accessor: "email",
        sortType: (rowA, rowB) => {
          const a = rowA.values.email?.toLowerCase() || "";
          const b = rowB.values.email?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Date Registered",
        accessor: "createdAt",
        Cell: ({ value }) => formatDate(value),
        sortType: (rowA, rowB) => {
          const a = rowA.values.createdAt?.toLowerCase() || "";
          const b = rowB.values.createdAt?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Total Applications",
        accessor: "totalApplications",
        Cell: ({ value }) => <span className="fw-semibold text-primary">{value || 0}</span>,
        sortType: (rowA, rowB) => {
          const a = rowA.values.totalApplications || 0;
          const b = rowB.values.totalApplications || 0;
          return a - b;
        }
      }
    ],
    []
  );

  return (
    <div className="subMain-content">
      <div className="page-header d-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="page-title mt-4">User Accounts</h1>
          {totalCount > 0 && (
            <p className="page-subtitle text-muted">
              {totalCount} {totalCount === 1 ? 'user account' : 'user accounts'} found in the system
            </p>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        data={data}
        filterInput={filterInput}
        setFilterInput={setFilterInput}
        pageCount={pageCount}
        loading={loading}
        onPageChange={handlePageChange}
        currentPage={pageIndex}
      />
    </div>
  );
}

export default RegisteredUsers;
